import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  Avatar,
  Container,
} from "@mui/material";
import Authenticated from "../components/Layouts/Authenticated";
import { useProfileUpdateContext } from "../contexts/ProfileUpdateContext";
import UpdateDetails from "../components/UpdateDetails/UpdateDetails";
import { ProfileUpdateProvider } from "../contexts/ProfileUpdateContext";
import { createDashboardStyles } from "./styles";
import { useTheme } from "@mui/material/styles";
import { getStaticAssetPath } from "../utils/staticAssets";
import { UserProfile } from "../types";

/**
 * Main component for the user profile update page.
 * It fetches user data and handles the rendering of different
 * update forms based on the selected tab.
 */
const UpdateUser = () => {
  const { status, profileData: user } = useProfileUpdateContext();
  const userLoading = status.loading;
  const userError = status.error;

  // Styles
  const theme = useTheme();
  const dashboardStyles = createDashboardStyles(theme);

  // Get user avatar
  const getUserAvatar = (user: UserProfile | null) => {
    if (user) {
      return getStaticAssetPath(user.image || "");
    }
    return "";
  };

  // Get user name
  const getUserName = (user: UserProfile | null) => {
    if (user) {
      return user.name || "";
    }
    return "";
  };

  // Get user first letter of name
  const getUserFirstLetter = (user: UserProfile | null) => {
    if (user) {
      if (user.name) {
        return user.name.charAt(0).toUpperCase();
      }
      return "";
    }
    return "";
  };

  if (userLoading) {
    return (
      <Authenticated>
        <CircularProgress sx={{ display: "block", margin: "auto", mt: 4 }} />
      </Authenticated>
    );
  }

  if (userError) {
    return (
      <Authenticated>
        <Alert severity="error" sx={{ mt: 4 }}>
          {userError}
        </Alert>
      </Authenticated>
    );
  }
  return (
    <Authenticated>
      <Container
        maxWidth="xl"
        sx={{ px: { xs: 1, sm: 2 }, py: { xs: 2, lg: 4 } }}
      >
        {/* Introduction Bar at top of Dashboard */}
        <Box sx={dashboardStyles.topSection}>
          <Card sx={dashboardStyles.introCard}>
            <Box sx={dashboardStyles.userInfoContainer}>
              {/* User Avatar */}
              <Box sx={dashboardStyles.userAvatarContainer}>
                <Avatar
                  src={getUserAvatar(user)}
                  alt={getUserName(user)}
                  sx={dashboardStyles.userAvatarContainer}
                >
                  {getUserFirstLetter(user)}
                </Avatar>
              </Box>
              {/* User Text */}
              <Box sx={dashboardStyles.userTextContainer}>
                <Typography variant="body2" sx={dashboardStyles.dashboardLabel}>
                  Update User
                </Typography>
                <Typography variant="h4" sx={dashboardStyles.welcomeMessage}>
                  Update your account details
                </Typography>
              </Box>
            </Box>
          </Card>
        </Box>
        <UpdateDetails />
      </Container>
    </Authenticated>
  );
};

export default UpdateUser;
